import React, { useState } from "react";
import { Table, Badge, Modal, Form, Button, Alert } from "react-bootstrap";
import { FaFileAlt, FaPlus, FaUpload } from "react-icons/fa";
import { useAuth } from "../../hooks/useAuth";

export const DocumentUploadModal = ({
  entity_id,
  entity,
  showModal,
  setShowModal,
  onDocumentAdded,
}) => {
  const { backendUrl } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    documentType: "",
    title: "",
    description: "",
    expiryDate: "",
    file: null,
  });

  const documentTypes = [
    { value: "license", label: "License" },
    { value: "registration", label: "Registration" },
    { value: "insurance", label: "Insurance" },
    { value: "inspection", label: "Inspection" },
    { value: "receipt", label: "Receipt" },
    { value: "invoice", label: "Invoice" },
    { value: "contract", label: "Contract" },
    { value: "photo", label: "Photo" },
    { value: "manual", label: "Manual" },
    { value: "other", label: "Other" },
  ];

  console.log("component initiated")

  const handleOpenModal = () => {
    setShowModal(true);
    setError(null);
    setSuccess(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      documentType: "",
      title: "",
      description: "",
      expiryDate: "",
      file: null,
    });
    setError(null);
    setSuccess(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError("File size must be less than 10MB");
        return;
      }
      setFormData((prev) => ({
        ...prev,
        file: file,
      }));
      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    // Validation
    if (!formData.documentType || !formData.title || !formData.file) {
      setError("Please fill in all required fields");
      return;
    }

    setUploading(true);

    try {
      const uploadData = new FormData();
      uploadData.append("entityType", entity);
      uploadData.append("entity_id", entity_id);
      uploadData.append("documentType", formData.documentType);
      uploadData.append("title", formData.title);
      uploadData.append("description", formData.description);
      uploadData.append("expiryDate", formData.expiryDate);
      uploadData.append("files", formData.file);

      const response = await fetch(`${backendUrl}/api/fleet/documents`, {
        method: "POST",
        body: uploadData,
        // Don't set Content-Type header - browser will set it with boundary
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to upload document");
      }

      const result = await response.json();
      setSuccess(true);

      // Call parent callback to refresh documents list
      if (onDocumentAdded) {
        onDocumentAdded(result.document);
      }

      // Close modal after short delay
      setTimeout(() => {
        handleCloseModal();
      }, 1500);
    } catch (err) {
      setError(err.message || "An error occurred while uploading the document");
    } finally {
      setUploading(false);
    }
  };
  return (
    <Modal show={showModal} onHide={handleCloseModal} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaUpload className="me-2" />
          Upload Document
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && (
            <Alert variant="success">Document uploaded successfully!</Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label>
              Document Type <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              name="documentType"
              value={formData.documentType}
              onChange={handleInputChange}
              required
            >
              <option value="">Select document type...</option>
              {documentTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Title <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Enter document title"
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter document description (optional)"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Expiry Date</Form.Label>
            <Form.Control
              type="date"
              name="expiryDate"
              value={formData.expiryDate}
              onChange={handleInputChange}
            />
            <Form.Text className="text-muted">
              Leave empty if document doesn't expire
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              File <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="file"
              onChange={handleFileChange}
              required
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
            />
            <Form.Text className="text-muted">
              Accepted formats: PDF, DOC, DOCX, JPG, PNG, GIF (Max 10MB)
            </Form.Text>
            {formData.file && (
              <div className="mt-2">
                <Badge bg="secondary">
                  {formData.file.name} ({(formData.file.size / 1024).toFixed(0)}{" "}
                  KB)
                </Badge>
              </div>
            )}
          </Form.Group>

          {/* <Alert variant="info" className="mb-0">
              <small>
                <strong>Driver:</strong> {entity.firstName} {entity.lastName} (ID: {entity.id})
              </small>
            </Alert> */}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseModal}
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={uploading}>
            {uploading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Uploading...
              </>
            ) : (
              <>
                <FaUpload className="me-1" /> Upload Document
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default DocumentUploadModal;
